import { lastValueFrom } from 'rxjs';
import { PersonProfileService } from 'src/app/people/profile/person-profile.service';
import { AttributeDefinition } from '../models/attributes/attributeDefinition';
import { AttributeEditorMode } from '../models/attributes/attributeEditorMode';
import { AttributeWrite } from '../models/attributes/attributeWrite';
import { ComplexAttributeWrite } from '../models/attributes/complexAttributeWrite';
import { Seat } from '../models/team/seat';
import { CustomFieldsService } from './custom-fields.service';
import { TeamService } from './team.service';
import { Injectable } from '@angular/core';
import { CredentialSingletonService } from './credential-singleton.service';
import { Attribute } from '../models/attributes/attribute';
import { IamPersonOrTeam } from 'src/app/people/models/IHaveAttributes';
import { AttributeService } from './attribute.service';
import { IGenericAttributeService } from 'src/shared-app-portal/interfaces/i-generic-attribute-service';
import { AttributeUiType } from '../models/attributes/attributeUiType';

@Injectable({
  providedIn: 'root'
})
export class GenericAttributeServiceFactory {
  constructor(
    private customFieldsService: CustomFieldsService,
    private personProfileService: PersonProfileService,
    private credentialSingletonService: CredentialSingletonService,
    private teamService: TeamService,
    private attributeService: AttributeService
  ) {}

  public BuildForTeamProfile = (
    teamId: string,
    attributeDefinition: AttributeDefinition,
    seat: Seat
  ): IGenericAttributeService =>
    new TeamSeatUpdateAttributeService(teamId, attributeDefinition, seat, this.teamService);

  public BuildForTeamWithCallback = (
    teamId: string,
    attributeDefinition: AttributeDefinition,
    seat: Seat,
    callback: () => void
  ): IGenericAttributeService =>
    new TeamSeatUpdateAttributeService(teamId, attributeDefinition, seat, this.teamService, () => {
      callback();
    });

  public BuildForPersonOnProfile = (
    personId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdatePersonAttributeService(personId, attributeDefinition, this.customFieldsService, () =>
      this.personProfileService.getPerson(personId)
    );

  public BuildForPersonWithCallback = (
    personId: string,
    attributeDefinition: AttributeDefinition,
    peopleHomeCallback: () => void
  ): IGenericAttributeService =>
    new UpdatePersonAttributeService(
      personId,
      attributeDefinition,
      this.customFieldsService,
      () => {
        this.personProfileService.getPerson(personId);
        peopleHomeCallback();
      }
    );

  public BuildForTeamWithoutSeatOnProfile = (
    teamId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdateTeamAttributeService(teamId, attributeDefinition, this.teamService);

  private getAttributeDefinitionAndExistingValue = async (
    attributeDefinitionId: string
  ): Promise<{ existingValue: Attribute[]; attributeDefinition: AttributeDefinition }> =>
    await Promise.all([
      this.credentialSingletonService.isTeam
        ? lastValueFrom(this.teamService.getTeamById(this.credentialSingletonService.getEntityId))
        : this.personProfileService.getPerson(this.credentialSingletonService.getEntityId, false),
      this.attributeService.getAttributeDefinitions()
    ]).then(([{ attributes }, attributeDefinitions]: [IamPersonOrTeam, AttributeDefinition[]]) => {
      const existingValue = attributes.filter(
        (a) => a.attributeDefinitionId == attributeDefinitionId
      );
      const attributeDefinition = attributeDefinitions.find((a) => a.id === attributeDefinitionId);
      return { existingValue, attributeDefinition };
    });

  public BuildForEntityOnEvidence = async (
    entityScenarioId: string,
    entityRequirementId: string,
    attributeDefinitionId: string
  ): Promise<{ service: IGenericAttributeService; existingValue: Attribute[] }> => {
    const { existingValue, attributeDefinition } =
      await this.getAttributeDefinitionAndExistingValue(attributeDefinitionId);

    if (this.credentialSingletonService.isPerson) {
      return {
        service: this.BuildForPersonOnEvidence(
          this.credentialSingletonService.getEntityId,
          entityScenarioId,
          entityRequirementId,
          attributeDefinition
        ),
        existingValue
      };
    }
    if (this.credentialSingletonService.isTeam) {
      return {
        service: this.BuildForTeamOnEvidence(
          this.credentialSingletonService.getEntityId,
          entityScenarioId,
          entityRequirementId,
          attributeDefinition
        ),
        existingValue
      };
    }
    throw new Error('Could not tell if this a person or team');
  };

  private BuildForPersonOnEvidence = (
    personId: string,
    entityScenarioId: string,
    entityRequirementId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdatePersonAttributeService(
      personId,
      attributeDefinition,
      this.customFieldsService,
      this.onScenarioEvidenceUpdated(entityScenarioId, entityRequirementId),
      this.onDocumentAttributeUpdated
    );

  private BuildForTeamOnEvidence = (
    teamId: string,
    entityScenarioId: string,
    entityRequirementId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdateTeamAttributeService(
      teamId,
      attributeDefinition,
      this.teamService,
      this.onScenarioEvidenceUpdated(entityScenarioId, entityRequirementId),
      this.onDocumentAttributeUpdated
    );

  public BuildForEntityOnSpecialtyEvidence = async (
    entityCredentialApplicationId: string,
    attributeDefinitionId: string
  ): Promise<{ service: IGenericAttributeService; existingValue: Attribute[] }> => {
    const { existingValue, attributeDefinition } =
      await this.getAttributeDefinitionAndExistingValue(attributeDefinitionId);

    if (this.credentialSingletonService.isPerson) {
      return {
        service: this.BuildForPersonOnSpecialtyEvidence(
          this.credentialSingletonService.getEntityId,
          entityCredentialApplicationId,
          attributeDefinition
        ),
        existingValue
      };
    }
    if (this.credentialSingletonService.isTeam) {
      return {
        service: this.BuildForTeamOnSpecialtyEvidence(
          this.credentialSingletonService.getEntityId,
          entityCredentialApplicationId,
          attributeDefinition
        ),
        existingValue
      };
    }
    throw new Error('Could not tell if this a person or team');
  };

  private BuildForPersonOnSpecialtyEvidence = (
    personId: string,
    personCredentialApplicationId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdatePersonAttributeService(
      personId,
      attributeDefinition,
      this.customFieldsService,
      this.onApplicationUpdated(personCredentialApplicationId),
      this.onDocumentAttributeUpdated
    );

  private BuildForTeamOnSpecialtyEvidence = (
    teamId: string,
    teamCredentialApplicationId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdateTeamAttributeService(
      teamId,
      attributeDefinition,
      this.teamService,
      this.onApplicationUpdated(teamCredentialApplicationId),
      this.onDocumentAttributeUpdated
    );

  private onApplicationUpdated = (entityCredentialApplicationId: string) => {
    return () => {
      this.credentialSingletonService.getSpecialtyEvidence(entityCredentialApplicationId);
      this.credentialSingletonService.queueEntityAvailableCredentialsAutomations();
    };
  };

  private onScenarioEvidenceUpdated = (entityScenarioId: string, entityRequirementId: string) => {
    return () => {
      this.credentialSingletonService.getScenarioEvidences(entityScenarioId, entityRequirementId);
      this.credentialSingletonService.queueEntityAvailableCredentialsAutomations();
    };
  };

  private onDocumentAttributeUpdated = () => {
    this.credentialSingletonService.documentAttributeUpdated.next();
  };
}

/**
 * Service for updating/deleting a team attribute (not seat-specific).
 * Implements IGenericAttributeService.
 */
class UpdateTeamAttributeService implements IGenericAttributeService {
  constructor(
    private teamId: string,
    public attributeDefinition: AttributeDefinition,
    private teamService: TeamService,
    private updateFunc: () => void = null,
    private onDocumentAttributeUpdated: () => void = null
  ) {
    this.entityId = teamId;
    this.mode = AttributeEditorMode.TeamDetails;
  }

  private onUpdate = (message: string) => {
    this.teamService.openSnackBar(message, true);
    if (this.updateFunc) {
      this.updateFunc();
    }
  };

  updateAttribute = async (attribute: AttributeWrite) => {
    const result = await this.teamService.updateTeamCustomField(
      this.teamId,
      this.attributeDefinition.id,
      attribute
    );

    if (result) {
      this.onUpdate('custom field value has been updated.');
      if (attribute.uiType === AttributeUiType.Document) {
        this.onDocumentAttributeUpdated?.();
      }
      return true;
    }
    return false;
  };

  updateAttributeComplex = () => {
    throw new Error('Teams do not support complex attributes.');
  };

  deleteAttribute = () =>
    this.teamService
      .deleteTeamCustomField(this.teamId, this.attributeDefinition.id)
      .subscribe(() => {
        this.onUpdate('custom field value has been deleted.');
        if (this.attributeDefinition.uiType === AttributeUiType.Document) {
          this.onDocumentAttributeUpdated?.();
        }
      });

  mode: AttributeEditorMode;
  entityId: string;
}

/**
 * Service for updating/deleting a team seat (member) attribute.
 * Implements IGenericAttributeService.
 */
class TeamSeatUpdateAttributeService implements IGenericAttributeService {
  constructor(
    private teamId: string,
    public attributeDefinition: AttributeDefinition,
    private seat: Seat,
    private teamService: TeamService,
    private updateFunc?: () => void
  ) {
    this.mode = AttributeEditorMode.TeamDetails;
    this.entityId = teamId;
  }
  private onUpdate = (message: string) => {
    this.teamService.openSnackBar(message, true);
    if (this.updateFunc) {
      this.updateFunc();
    }
  };

  updateAttribute = async (attribute: AttributeWrite) => {
    const result = await this.teamService.updateTrackedFieldValue(
      this.teamId,
      this.seat,
      attribute.attributeDefinitionId,
      attribute
    );

    if (result) {
      this.onUpdate('Tracked field value has been updated.');
    }
    return result;
  };

  updateAttributeComplex = (_: ComplexAttributeWrite[]) => {
    throw Error('no complex attributes on teams');
  };

  deleteAttribute = () => {
    this.teamService
      .deleteTrackedFieldValue(this.teamId, this.seat, this.attributeDefinition.id)
      .subscribe(() => {
        this.onUpdate('Tracked field value has been deleted.');
      });
  };
  mode: AttributeEditorMode;
  entityId: string;
}

/**
 * Service for updating/deleting a person attribute.
 * Implements IGenericAttributeService.
 */
class UpdatePersonAttributeService implements IGenericAttributeService {
  constructor(
    private personId: string,
    public attributeDefinition: AttributeDefinition,
    private customFieldsService: CustomFieldsService,
    private updateFunc: () => void,
    private onDocumentAttributeUpdated: () => void = null
  ) {
    this.entityId = personId;
    this.mode = AttributeEditorMode.PersonDetails;
  }

  private onUpdate = (message: string) => {
    this.customFieldsService.openSnackBar(message, true);
    this.updateFunc();
  };

  updateAttribute = async (attribute: AttributeWrite) => {
    const result = await this.customFieldsService.updateAttribute(
      this.personId,
      this.attributeDefinition.id,
      attribute
    );
    if (result) {
      this.onUpdate('custom field value has been updated.');
      if (attribute.uiType === AttributeUiType.Document) {
        this.onDocumentAttributeUpdated?.();
      }
      return true;
    }
    return false;
  };

  updateAttributeComplex = (attributes: ComplexAttributeWrite[]) =>
    this.customFieldsService
      .addOrUpdateComplexAttribute(this.personId, this.attributeDefinition.id, attributes)
      .subscribe(() => {
        this.onUpdate('custom field value has been updated.');
      });

  deleteAttribute = () =>
    this.customFieldsService
      .deleteAttribute(this.personId, this.attributeDefinition.id)
      .subscribe(() => {
        this.onUpdate('custom field value has been deleted.');
        if (this.attributeDefinition.uiType === AttributeUiType.Document) {
          this.onDocumentAttributeUpdated?.();
        }
      });

  mode: AttributeEditorMode;
  entityId: string;
}
